<template>
  <LazyHomeCommonLayout v-if="isOnLoad">
    <template #content>
      <LazyProfileContent
        :is-share="isShare"
        :profile-menu-open-flag="profileMenuOpenFlag"
        @on-change-popup-message="openToastMessage"
        @on-change-open-flag="onChangeOpenFlag"
        @on-change-public-flag="onChangePublicFlag"
      />
    </template>
  </LazyHomeCommonLayout>
</template>

<script setup lang="ts">
import { useUserStore } from "~~/application/store/user/userStore";
import { setCookie } from "~~/package/utils/CookieUtil";
import { useProfileService } from "~~/application/composables/service/useProfileService";
import { ProfileMenuOpenFlagModel } from "~~/package/mapper/profile/model/ProfileUserInformationModel";
import { ProfileMenuType } from "~~/package/mapper/profile/model/ProfileMenuType";
import MessageList from "~~/application/components/Profile/enum/MessageList";
import { ToastType } from "~~/application/components/Common/Toast/model/ToastType";
import { useI18n } from "vue-i18n";
import { useToastStore } from "~~/application/store/toast/toastStore";
import { useProfileStore } from "~~/application/store/profile/profileStore";
import { UserStatusType } from "~~/package/mapper/user/model/UserStatusType";
import { useGenericDialog } from "~/composables/useGenericDialog";
import { CookieKeyType } from "~~/package/mapper/common/model/CookieKeyType";
import { SignType, SignUIType } from "~/components/Sign/enum/SignType";

const { t } = useI18n();
const { $i18n } = useNuxtApp();
const lang = computed(() =>
  $i18n.locale.value.toUpperCase()
);
const route = useRoute();
const router = useRouter();

const userStore = useUserStore();
const toastStore = useToastStore();
const profileStore = useProfileStore();
const { openGenericAlert } = useGenericDialog();

const profileService = useProfileService();
const profileMenuOpenFlag = ref<ProfileMenuOpenFlagModel>(new ProfileMenuOpenFlagModel());
const isShare = ref(false);
const isOnLoad = ref(false);

const getUserProfileData = (isShare: boolean) => {
  if (isShare) {
    profileService.getUserShareProfile(route.query.uuid as string, lang.value).then(() => {
      Promise.all([
        profileService.getUserShareProfile(route.query.uuid as string, lang.value),
        profileService.getUserWorkExperienceList(isShare, lang.value, (route.query.uuid as string)),
        profileService.getUserEducationList(isShare, lang.value, (route.query.uuid as string))
      ]).then(() => {
        initProfile();
      }).catch(() => {
        initProfile();
      });
    })
  } else {
    profileService.getUserMyProfile(lang.value).then(() => {
      Promise.all([
        profileService.getUserWorkExperienceList(isShare, lang.value, (route.query.uuid as string)),
        profileService.getUserEducationList(isShare, lang.value, (route.query.uuid as string))]
      ).then(() => {
        isOnLoad.value = true;
      }).catch(() => {
        isOnLoad.value = true;
      });
    })
  }
};

const initProfile = () => {
  if(profileStore.userInfo.userStatus === UserStatusType.LEAVE){
    openGenericAlert(
      { title: t("profile-access-limit"), contents: t("profile-access-limit-description"), popupClass: 'dark' },
      {
        confirm: { title: t("common-ok"), event: redirectToDashboardOrHome },
        close: { event: redirectToDashboardOrHome }
      }
    );
  } else {
    isOnLoad.value = true;
  }
}

const redirectToDashboardOrHome = () => {
  const targetRoute = userStore.userV2.id ? "/dashboard" : "/";
  router.push(targetRoute);
};

const onChangeOpenFlag = (flag: boolean, menuType: ProfileMenuType) => {
  profileMenuOpenFlag.value = new ProfileMenuOpenFlagModel();

  switch (menuType) {
    case ProfileMenuType.FULL_NAME:
      profileMenuOpenFlag.value.fullNameFlag = flag;
      break;
    case ProfileMenuType.EMAIL:
      profileMenuOpenFlag.value.emailExposureFlag = flag;
      break;
    case ProfileMenuType.COUNTRY:
      profileMenuOpenFlag.value.regionFlag = flag;
      break;
    case ProfileMenuType.PHONE_NUMBER:
      profileMenuOpenFlag.value.phoneNumberFlag = flag;
      break;
    case ProfileMenuType.GENDER:
      profileMenuOpenFlag.value.genderFlag = flag;
      break;
    case ProfileMenuType.LINK:
      profileMenuOpenFlag.value.linksFlag = flag;
      break;
    case ProfileMenuType.SKILL:
      profileMenuOpenFlag.value.skillsFlag = flag;
      break;
    case ProfileMenuType.ABOUT_ME:
      profileMenuOpenFlag.value.aboutMeFlag = flag;
      break;
    case ProfileMenuType.ACTIVITY_HISTORIES:
      profileMenuOpenFlag.value.activityHistoriesFlag = flag;
      break;
    case ProfileMenuType.WORK_EXPERIENCE:
      profileMenuOpenFlag.value.workExperienceFlag = flag;
      break;
    case ProfileMenuType.EDUCATION:
      profileMenuOpenFlag.value.educationFlag = flag;
      break;
  }
};

const onChangePublicFlag = (flag: boolean, menuType: ProfileMenuType) => {
  switch (menuType) {
    case ProfileMenuType.FULL_NAME:
      profileStore.userInfo.fullNameFlag = flag;
      profileMenuOpenFlag.value.fullNameFlag = false;
      break;
    case ProfileMenuType.JOB_OFFER:
      profileStore.userInfo.jobOfferFlag = flag;
      break;
    case ProfileMenuType.EMAIL:
      profileStore.userInfo.emailExposureFlag = flag;
      profileMenuOpenFlag.value.emailExposureFlag = false;
      break;
    case ProfileMenuType.COUNTRY:
      profileStore.userInfo.regionFlag = flag;
      profileMenuOpenFlag.value.regionFlag = false;
      break;
    case ProfileMenuType.PHONE_NUMBER:
      profileStore.userInfo.phoneNumberFlag = flag;
      profileMenuOpenFlag.value.phoneNumberFlag = false;
      break;
    case ProfileMenuType.GENDER:
      profileStore.userInfo.genderFlag = flag;
      profileMenuOpenFlag.value.genderFlag = false;
      break;
    case ProfileMenuType.LINK:
      profileStore.userInfo.linksFlag = flag;
      profileMenuOpenFlag.value.linksFlag = false;
      break;
    case ProfileMenuType.SKILL:
      profileStore.userInfo.skillsFlag = flag;
      profileMenuOpenFlag.value.skillsFlag = false;
      break;
    case ProfileMenuType.ABOUT_ME:
      profileStore.userInfo.aboutMeFlag = flag;
      profileMenuOpenFlag.value.aboutMeFlag = false;
      break;
    case ProfileMenuType.ACTIVITY_HISTORIES:
      profileStore.userInfo.activityHistoriesFlag = flag;
      profileMenuOpenFlag.value.activityHistoriesFlag = false;
      break;
    case ProfileMenuType.WORK_EXPERIENCE:
      profileStore.userInfo.workExperienceFlag = flag;
      profileMenuOpenFlag.value.workExperienceFlag = false;
      break;
    case ProfileMenuType.EDUCATION:
      profileStore.userInfo.educationFlag = flag;
      profileMenuOpenFlag.value.educationFlag = false;
      break;
  }

  profileService.modifyUserProfile(profileStore.userInfo).then(response => {
    if (response.id > 0) {
      profileStore.userInfo = response;
      openToastMessage(
        true,
        menuType === ProfileMenuType.JOB_OFFER ? MessageList.successChange :
          flag ? MessageList.public : MessageList.private
      );
    } else {
      openToastMessage(
        false,
        MessageList.failChange
      );
    }
  }).catch(() => {
    openToastMessage(
      false,
      MessageList.failChange
    );
  });
};

const openToastMessage = (classType: boolean, messageType: MessageList) => {
  toastStore.openToast({
    toastType: classType ? ToastType.SUCCESS : ToastType.FAILED,
    contents: t(messageType)
  });
};

const handleRouteUuidChange = () => {
  isShare.value = route.query.uuid !== undefined;
  if (!isShare.value) {
    if (userStore.userV2.uuid === "") {
      setCookie(CookieKeyType.REDIRECT_URL, {
        fullPath: route.fullPath,
        path: route.path,
        query: route.query,
        params: route.params,
        isManager: false
      }, {
        expires: new Date(Date.now() + 86400 * 1000),
        maxAge: 86400
      });
      router.replace({
        path: "/sign",
        query: {
          uiType: SignUIType.LOGIN,
          type: SignType.CANDIDATE
        }
      });
      return;
    }
    if (!userStore.userV2.location) {
      router.replace({ path: "/register/info" });
      return;
    }
    getUserProfileData(false);
  }

  else if (userStore.userV2.uuid === route.query.uuid) {
    isShare.value = false;
    if (!userStore.userV2.location) {
      router.replace({ path: "/register/info" });
    } else {
      getUserProfileData(false);
      router.replace("/profile");
    }
  }
  else {
    getUserProfileData(true);
  }
};

onMounted(() => {
  handleRouteUuidChange();
});

watch(() => route.query.uuid, () => {
  handleRouteUuidChange();
});
</script>

<style lang="scss">
@import "~/assets/scss/abstracts/variables.scss";
@import "~/assets/scss/abstracts/mixin.scss";
@import "~/assets/scss/profile.scss";
</style>